import React, { useCallback, useMemo, useEffect } from 'react';
import { ResponseErrorPanel, TableColumn } from '@backstage/core-components';
import { CopyableText } from '../../CopyableText';
import { ModernResourceView } from '../../ModernResourceView';
import { GcpActionMenu, GcpActionConfirmationDialog } from '../actions';
import { GcpVmTableData } from './types';
import { mapResourcesToGcpVmTableData } from './utils';
import { ExecuteActionOptions } from '../../../api';
import {
  GcpVmResourceDetails,
  CRDGCPConfig,
} from '@internal/backstage-plugin-compute-resource-discovery-new-common';
import { useApi, errorApiRef, alertApiRef } from '@backstage/core-plugin-api';
import { useActionManagement } from './useActionManagement';
import { useGcpContext } from '../GcpContext';
import useAsyncRetry from 'react-use/lib/useAsyncRetry';

interface GcpTableProps {
  entityRef: string;
  gcpConfig: CRDGCPConfig;
  title: string;
}

export const GcpVmTable: React.FC<GcpTableProps> = ({
  entityRef,
  gcpConfig,
  title,
}) => {
  const { api, projectCode, refreshContext } = useGcpContext();

  const executeAction = useCallback(
    (options: ExecuteActionOptions) => {
      return api.executeAction(options);
    },
    [api],
  );

  const {
    value: resources,
    loading: isLoading,
    error: error,
    retry: refresh,
  } = useAsyncRetry(async () => {
    if (!projectCode) return [];
    return api.listResources({
      serviceType: 'gcp',
      resourceType: 'vm',
      projectCode: projectCode,
      entityRef: entityRef,
      cloudConfig: gcpConfig,
    });
  }, [projectCode, api]);

  const handleRefresh = () => {
    // Invalidate cache for this specific GCP VM configuration
    api.invalidateCache('listResources');
    refresh();
  };

  const errorApi = useApi(errorApiRef);
  const alertApi = useApi(alertApiRef);

  const {
    menuPosition,
    confirmationDialogOpen,
    selectedAction,
    isActionInProgress,
    handleTableActionClick,
    handleMenuClose,
    handleActionClick,
    handleConfirmAction,
    setConfirmationDialogOpen,
  } = useActionManagement({
    executeAction,
    refresh,
    errorApi,
    alertApi,
    gcpConfig,
    entityRef,
    projectCode,
  });

  useEffect(() => {
    if (!refreshContext) return;

    const refreshKey = `gcp:vm:${gcpConfig.projectId}`;
    const unregister = refreshContext.registerRefresh(refreshKey, refresh);

    return unregister;
  }, [refreshContext, refresh, gcpConfig.projectId]);

  const columns = useMemo<TableColumn<GcpVmTableData>[]>(
    () => [
      {
        title: 'ID',
        field: 'identifier',
        render: (row: GcpVmTableData) => (
          <div style={{ maxWidth: '400px' }}>
            <CopyableText value={row.identifier} />
          </div>
        ),
      },
      {
        title: 'Name',
        field: 'name',
      },
      {
        title: 'Zone',
        field: 'zone',
      },
      {
        title: 'Machine Type',
        field: 'machineType',
      },
      {
        title: 'Status',
        field: 'status',
      },
    ],
    [],
  );

  const data = useMemo(
    () =>
      resources
        ? mapResourcesToGcpVmTableData(resources as GcpVmResourceDetails[])
        : [],
    [resources],
  );

  if (error) {
    error.message = `Failed to load GCP VMs`;
    return <ResponseErrorPanel error={error} title={error.message} />;
  }

  return (
    <>
      <ModernResourceView<GcpVmTableData>
        title={title}
        data={data}
        isLoading={isLoading}
        onRefresh={handleRefresh}
        onActions={handleTableActionClick}
        cloudService="gcp"
        resourceType="vm"
        columns={columns}
        colorTheme="#D32F2F" // GCP Red
        emptyMessage="No VM instances found"
      />

      <GcpActionMenu
        handleActionClick={handleActionClick}
        handleMenuClose={handleMenuClose}
        handleOpenConnection={() => {}}
        menuPosition={menuPosition}
        service="vm"
      />

      <GcpActionConfirmationDialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        onConfirm={handleConfirmAction}
        action={selectedAction as any}
        isLoading={isActionInProgress}
        service="vm"
      />
    </>
  );
};
